import { useCallback, useEffect, useMemo, useState } from 'react';
import { aws } from '../lib/awsdb';

// HarryBox — versatile inline component that asks Harry "where are
// things at?" for a given scope (relationship or task). Two depths:
// shallow (~3s, ~$0.005, single Sonnet call) and deep (~30-60s, ~$0.05,
// full openclaw run on Harry's EC2). Manual cache only — never auto-
// refreshes, never polls. User clicks Shallow or Deep to regenerate.
//
// States rendered (from spec):
//   empty           — no cached brief, no fetch in flight
//   cached          — brief on disk, both buttons available
//   loading-shallow — Sonnet call in flight
//   loading-deep    — Harry openclaw run in flight (slower)
//   error           — last call failed; show retry
//   found-nothing   — brief text starts with "could not find anything on"
//
// Voice + format are baked into the Lambda's system prompt — the
// component never reshapes or post-processes the brief text.

const BRIEF_URL = import.meta.env.VITE_AWS_HARRY_BRIEF_URL;
const HEADERS = { 'Content-Type': 'application/json' };

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function isFoundNothing(text) {
  if (!text) return false;
  return /^could not find anything on /i.test(text.trim());
}

// Source-count label for the subtitle line. Shape changed 2026-05-07:
//
//   - Shallow briefs include sources[] (the relationship_updates feed
//     that was stitched into the prompt). Count those — that's a faithful
//     citation list.
//   - Deep briefs set harry_searched_live=true because Harry called his
//     moreece-signals tool on the fly and we don't get a tool-call list
//     back through SSM. Don't show "0 sources" — render "live search"
//     instead, which is honest.
function sourcesLabel(sourcesJson) {
  if (!sourcesJson) return '0 sources';
  if (sourcesJson.harry_searched_live) return 'searched live';
  if (Array.isArray(sourcesJson?.sources)) {
    return `${sourcesJson.sources.length} sources`;
  }
  return '0 sources';
}

export default function HarryBox({ scopeKind, scopeId, contextLabel, session, defaultCollapsed = false }) {
  const [brief, setBrief] = useState(null);          // { brief_text, generated_at, sources_json, depth, model, cost_usd }
  const [loading, setLoading] = useState(null);      // 'shallow' | 'deep' | null
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [hydrating, setHydrating] = useState(true);

  // Initial cache hydrate. Reads the harry_briefs row directly via
  // PostgREST (RLS allows any logged-in user). Falls back to nothing
  // on miss — the empty state is fine.
  useEffect(() => {
    if (!scopeKind || !scopeId) return;
    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        const { data } = await aws.from('harry_briefs')
          .select('brief_text, generated_at, sources_json, depth, model, cost_usd')
          .eq('scope_kind', scopeKind)
          .eq('scope_id', scopeId)
          .maybeSingle();
        if (cancelled) return;
        if (data) {
          setBrief({
            brief_text: data.brief_text,
            generated_at: data.generated_at,
            sources_json: data.sources_json,
            depth: data.depth,
            model: data.model,
            cost_usd: Number(data.cost_usd ?? 0),
          });
        }
      } catch (e) {
        // Cache hydrate failure is non-fatal — user can still click a button.
        console.warn('[HarryBox] cache hydrate failed:', e?.message);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKind, scopeId]);

  const callBrief = useCallback(async (depth) => {
    if (!session?.code || !BRIEF_URL) {
      setError('Not signed in or HarryBox endpoint missing.');
      return;
    }
    setLoading(depth);
    setError(null);
    try {
      const res = await fetch(BRIEF_URL, {
        method: 'POST',
        headers: HEADERS,
        body: JSON.stringify({
          scope_kind: scopeKind,
          scope_id: scopeId,
          depth,
          refresh: true,             // user click always means regenerate
          userPin: session.code,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setBrief({
        brief_text: json.brief_text,
        generated_at: json.generated_at,
        sources_json: json.sources_json,
        depth: json.depth,
        model: json.model,
        cost_usd: Number(json.cost_usd ?? 0),
      });
    } catch (e) {
      setError(e?.message || 'Brief failed');
    } finally {
      setLoading(null);
    }
  }, [scopeKind, scopeId, session?.code]);

  const state = useMemo(() => {
    if (loading === 'shallow') return 'loading-shallow';
    if (loading === 'deep') return 'loading-deep';
    if (error) return 'error';
    if (!brief) return 'empty';
    if (isFoundNothing(brief.brief_text)) return 'found-nothing';
    return 'cached';
  }, [loading, error, brief]);

  if (hydrating) return null;        // avoid empty-flash on first paint

  if (collapsed) {
    return (
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => setCollapsed(false)}
        style={{ marginBottom: 8 }}
      >
        Open HarryBox
      </button>
    );
  }

  // ── Header bar ──
  const subtitle = brief
    ? `Updated ${formatRelative(brief.generated_at)} · ${sourcesLabel(brief.sources_json)} · ${brief.depth}`
    : 'No brief yet — Harry hasn\'t looked for this one.';

  // ── Action buttons ──
  const ShallowBtn = (
    <button
      type="button"
      className="btn btn-primary btn-sm"
      disabled={!!loading}
      onClick={() => callBrief('shallow')}
      title="One Sonnet call against what we already have on file. ~3s, ~$0.005."
    >
      {loading === 'shallow' ? 'Reading…' : 'Shallow'}
    </button>
  );
  const DeepBtn = (
    <button
      type="button"
      className="btn btn-secondary btn-sm"
      disabled={!!loading}
      onClick={() => callBrief('deep')}
      title="Full Harry session with tools. ~30–60s, ~$0.05."
    >
      {loading === 'deep' ? 'Harry digging…' : 'Deep dive'}
    </button>
  );

  // ── Body ──
  let body;
  if (state === 'empty') {
    body = (
      <div style={{ fontSize: 13, color: 'var(--text-subdued)' }}>
        Click <b>Shallow</b> for a quick read or <b>Deep dive</b> for the full Harry treatment (~30–60s).
      </div>
    );
  } else if (state === 'loading-shallow') {
    body = (
      <div style={{ fontSize: 13, color: 'var(--text-subdued)' }}>
        Reading what's on file<span className="dots">…</span>
      </div>
    );
  } else if (state === 'loading-deep') {
    body = (
      <div style={{ fontSize: 13, color: 'var(--text-subdued)' }}>
        Harry is digging — Slack, email, transcripts. ~30–60s. Sit tight.
      </div>
    );
  } else if (state === 'error') {
    body = (
      <div style={{ fontSize: 13, color: '#b91c1c' }}>
        Couldn't generate brief: {error}
      </div>
    );
  } else if (state === 'found-nothing') {
    body = (
      <div style={{ fontSize: 13, color: 'var(--text-emphasized)', whiteSpace: 'pre-wrap' }}>
        {brief.brief_text}
      </div>
    );
  } else {
    body = (
      <div style={{ fontSize: 13, color: 'var(--text-emphasized)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {brief.brief_text}
      </div>
    );
  }

  // ── Sources collapsible ──
  const srcs = Array.isArray(brief?.sources_json?.sources) ? brief.sources_json.sources : [];

  return (
    <div
      className="harrybox"
      style={{
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border-subdued)',
        borderRadius: 12,
        padding: '12px 14px',
        marginBottom: 12,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-emphasized)' }}>HarryBox</span>
          {contextLabel ? (
            <span style={{ fontSize: 11, color: 'var(--text-disabled)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contextLabel}</span>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Collapse HarryBox"
          onClick={() => setCollapsed(true)}
          style={{ background: 'transparent', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4 }}
        >
          ×
        </button>
      </div>

      <div style={{ marginBottom: 10 }}>{body}</div>

      <div style={{ fontSize: 11, color: 'var(--text-disabled)', marginBottom: 10 }}>
        {subtitle}
        {srcs.length > 0 ? (
          <button
            type="button"
            onClick={() => setSourcesOpen(o => !o)}
            style={{ marginLeft: 8, background: 'transparent', border: 'none', color: 'var(--text-subdued)', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, padding: 0 }}
          >
            {sourcesOpen ? 'hide' : 'show'} sources
          </button>
        ) : null}
      </div>

      {sourcesOpen && srcs.length > 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text-subdued)', marginBottom: 10, paddingLeft: 8, borderLeft: '2px solid var(--border-subdued)' }}>
          {srcs.map((s, i) => {
            const k = s.kind || 'source';
            const ref = s.ref;
            const refLabel = ref?.source_type ? `${ref.source_type}:${ref.source_id}` : (ref?.source_id || '');
            return (
              <div key={s.id || i} style={{ marginBottom: 4 }}>
                <b style={{ marginRight: 6 }}>[{k}]</b>
                {refLabel ? <span style={{ marginRight: 6, fontFamily: 'ui-monospace, monospace' }}>{refLabel}</span> : null}
                <span>{s.preview || ''}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {ShallowBtn}
        {DeepBtn}
      </div>
    </div>
  );
}
